/*
 * Nuvoro cart drawer
 * - <cart-drawer>          open/close, Section Rendering refresh, hidden add-ons (upsell toggles, gifts)
 * - <cart-reserve-timer>   "cart reserved for mm:ss" countdown shared by every render of the drawer
 * - <cart-terms-checkbox>  optional "I agree" checkbox guarding the checkout button
 * Contract (used by product forms): open(opener), close(), getSectionsToRender(), renderContents(state, keepClosed).
 */
(() => {
  if (customElements.get('cart-drawer')) return;

  const RESTART_SECONDS = 90;
  const MAX_AUTO_SYNC = 6;

  const cartApi = () => window.NuvoroCart;

  const escapeValue = (value) => (window.CSS && CSS.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, '\\$&'));

  /*
   * One clock per page load. It starts the first time the drawer opens, keeps running while the
   * drawer markup is re-rendered, and after 00:01 starts over at 01:30. Nothing is persisted.
   */
  const reserveClock = {
    value: null,
    interval: null,
    views: new Set(),
    setup(seconds) {
      if (this.value !== null) return;
      this.value = seconds > 0 ? seconds : RESTART_SECONDS;
    },
    start() {
      if (this.interval || this.value === null) return;
      this.interval = setInterval(() => {
        this.value = this.value > 1 ? this.value - 1 : RESTART_SECONDS;
        this.views.forEach((view) => view.show(this.value));
      }, 1000);
    },
    reset() {
      clearInterval(this.interval);
      this.interval = null;
      this.value = null;
    },
  };

  const twoDigits = (number) => String(number).padStart(2, '0');

  class CartReserveTimer extends HTMLElement {
    connectedCallback() {
      reserveClock.setup(parseInt(this.dataset.duration, 10) || 0);
      reserveClock.views.add(this);
      this.show(reserveClock.value);
    }

    disconnectedCallback() {
      reserveClock.views.delete(this);
    }

    show(seconds) {
      const minutes = this.querySelector('[data-timer-minutes]');
      const rest = this.querySelector('[data-timer-seconds]');
      if (minutes) minutes.textContent = twoDigits(Math.floor(seconds / 60));
      if (rest) rest.textContent = twoDigits(seconds % 60);
    }
  }

  class CartTermsCheckbox extends HTMLElement {
    constructor() {
      super();
      this.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        this.toggle();
      });
      this.addEventListener('keydown', (event) => {
        if (event.key !== ' ' && event.key !== 'Enter') return;
        event.preventDefault();
        this.toggle();
      });
    }

    connectedCallback() {
      this.reflect();
    }

    get checked() {
      return this.dataset.checked === 'true';
    }

    set checked(value) {
      this.dataset.checked = String(value);
      this.setAttribute('aria-checked', String(value));
      this.reflect();
    }

    toggle() {
      this.checked = !this.checked;
      const drawer = this.closest('cart-drawer');
      if (drawer) drawer.memory.termsAccepted = this.checked;
      if (this.checked) this.showWarning(false);
    }

    reflect() {
      const button = document.getElementById('CartDrawer-Checkout');
      if (button && this.dataset.disableButton === 'true') button.classList.toggle('disabled', !this.checked);
    }

    showWarning(visible) {
      const drawer = this.closest('cart-drawer');
      const warning = drawer && drawer.querySelector('[data-terms-warning]');
      if (warning) warning.classList.toggle('hidden', !visible);
    }
  }

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      // Client choices that must survive re-renders (cleared on page load, like before).
      this.memory = { toggles: new Map(), openDetails: new Set(), termsAccepted: false, scrollTop: 0 };
      this.autoSyncCount = 0;
      this.pending = 0;
    }

    get sectionId() {
      return this.dataset.sectionId || 'cart-drawer';
    }

    get isOpen() {
      return this.classList.contains('active');
    }

    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;

      this.addEventListener('keyup', (event) => {
        if (event.key === 'Escape' && this.isOpen) this.close();
      }, { signal });
      this.addEventListener('click', (event) => this.onInsideClick(event), { signal });
      document.addEventListener('click', (event) => this.onTriggerClick(event), { signal });
      document.addEventListener('keydown', (event) => this.onTriggerKey(event), { signal });
      // Runs before any document-level capture listener, so an unchecked terms box really blocks checkout.
      window.addEventListener('click', (event) => this.guardCheckout(event), { capture: true, signal });

      this.decorateTriggers();

      if (window.Shopify && window.Shopify.designMode) {
        // Section reloads in the editor start the clock from the (possibly changed) duration.
        reserveClock.reset();
        this.querySelectorAll('cart-reserve-timer').forEach((timer) => {
          if (typeof timer.show === 'function') timer.connectedCallback();
        });
        document.addEventListener('shopify:section:select', (event) => {
          if (event.detail.sectionId === this.sectionId) this.open();
        }, { signal });
        document.addEventListener('shopify:block:select', (event) => {
          if (event.detail.sectionId === this.sectionId) this.open();
        }, { signal });
        document.addEventListener('shopify:section:deselect', (event) => {
          if (event.detail.sectionId === this.sectionId && this.dataset.testMode !== 'true') this.close();
        }, { signal });
      }

      // Gifts / pre-selected add-ons may need to be added or removed for the cart as loaded.
      this.reconcile();
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
    }

    /* ---------- opening & closing ---------- */

    triggers() {
      const bubble = document.getElementById('cart-icon-bubble');
      const list = [...document.querySelectorAll('.header__icon--cart, [data-cart-drawer-open]')];
      if (bubble) list.push(bubble.closest('a, button') || bubble);
      return list.filter((element, index) => element && list.indexOf(element) === index && !this.contains(element));
    }

    decorateTriggers() {
      this.triggers().forEach((trigger) => {
        trigger.setAttribute('aria-haspopup', 'dialog');
        if (trigger.id === 'cart-icon-bubble' && !trigger.matches('a, button')) {
          trigger.setAttribute('role', 'button');
          trigger.setAttribute('tabindex', '0');
        }
      });
    }

    onTriggerClick(event) {
      const trigger = event.target.closest('#cart-icon-bubble, .header__icon--cart, [data-cart-drawer-open]');
      if (!trigger || this.contains(trigger)) return;
      event.preventDefault();
      this.open(trigger.closest('a, button') || trigger);
    }

    onTriggerKey(event) {
      if (event.code !== 'Space' && event.key !== ' ') return;
      const trigger = event.target.closest('#cart-icon-bubble[role="button"], .header__icon--cart');
      if (!trigger || this.contains(trigger)) return;
      event.preventDefault();
      this.open(trigger);
    }

    setActiveElement(element) {
      this.activeElement = element;
    }

    open(opener) {
      if (opener) this.setActiveElement(opener);
      this.prepareNoteAccessibility();

      const inner = this.querySelector('.drawer__inner');
      const focusDrawer = () => {
        const container = this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : document.getElementById('CartDrawer');
        const target = inner || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function' && container) trapFocus(container, target);
        else if (target) target.focus();
      };
      const animates = inner && !this.isOpen && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (animates) {
        inner.addEventListener('transitionend', focusDrawer, { once: true });
      } else {
        setTimeout(focusDrawer, 50);
      }

      // Next frame so the transition runs when the drawer was just rendered.
      requestAnimationFrame(() => this.classList.add('animate', 'active'));
      document.body.classList.add('overflow-hidden');
      reserveClock.start();
    }

    close() {
      this.classList.remove('active');
      if (typeof removeTrapFocus === 'function') removeTrapFocus(this.activeElement);
      else if (this.activeElement) this.activeElement.focus();
      document.body.classList.remove('overflow-hidden');
    }

    prepareNoteAccessibility() {
      const summary = this.querySelector('details > summary');
      if (!summary || summary.hasAttribute('role')) return;
      const details = summary.parentElement;
      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', String(details.open));
      if (details.id) summary.setAttribute('aria-controls', details.id);
      details.addEventListener('toggle', () => summary.setAttribute('aria-expanded', String(details.open)));
    }

    onInsideClick(event) {
      if (event.target.closest('[data-cart-drawer-close]') || event.target.id === 'CartDrawer-Overlay') {
        this.close();
        return;
      }
      const checkout = event.target.closest('#CartDrawer-Checkout');
      if (checkout && !checkout.disabled && !checkout.classList.contains('disabled')) {
        checkout.classList.add('loading');
        setTimeout(() => {
          if (window.location.pathname.includes('/checkout')) return;
          checkout.classList.remove('loading');
        }, 8000);
      }
    }

    guardCheckout(event) {
      const button = event.target.closest && event.target.closest('#CartDrawer-Checkout');
      if (!button || !this.contains(button)) return;
      const terms = this.querySelector('cart-terms-checkbox');
      if (!terms || terms.checked) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      terms.showWarning(true);
    }

    /* ---------- rendering ---------- */

    getSectionsToRender() {
      return [
        { id: this.sectionId, section: this.sectionId, selector: '#CartDrawer' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }

    renderContents(parsedState, keepClosed = false) {
      const sections = parsedState && parsedState.sections;
      if (!sections || !sections[this.sectionId]) {
        return this.refresh().then(() => {
          if (!keepClosed) this.open();
        });
      }
      if (!keepClosed) this.autoSyncCount = 0;
      this.paint(sections);
      if (!keepClosed) this.open();
      return Promise.resolve();
    }

    async refresh() {
      const api = cartApi();
      if (!api) return;
      try {
        const sections = await api.fetchSections(this.getSectionsToRender().map((entry) => entry.section));
        this.paint(sections);
      } catch (error) {
        console.error(error);
      }
    }

    paint(sections) {
      this.remember();
      const html = sections[this.sectionId];
      if (html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshHost = doc.querySelector('cart-drawer');
        const freshDrawer = doc.getElementById('CartDrawer');
        const currentDrawer = this.querySelector('#CartDrawer');
        if (freshDrawer && currentDrawer) currentDrawer.innerHTML = freshDrawer.innerHTML;
        if (freshHost) this.classList.toggle('is-empty', freshHost.classList.contains('is-empty'));
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && bubble) {
        const source = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }
      this.restore();
      this.prepareNoteAccessibility();
      this.reconcile();
    }

    remember() {
      this.memory.openDetails = new Set([...this.querySelectorAll('details[open][id]')].map((details) => details.id));
      const body = this.querySelector('.cart-drawer__body');
      this.memory.scrollTop = body ? body.scrollTop : 0;
    }

    restore() {
      this.memory.openDetails.forEach((id) => {
        const details = document.getElementById(id);
        if (details && this.contains(details)) details.open = true;
      });
      const body = this.querySelector('.cart-drawer__body');
      if (body) body.scrollTop = this.memory.scrollTop;

      this.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift').forEach((element) => {
        const saved = this.memory.toggles.get(element.dataset.handle);
        if (!saved) return;
        if (saved.variantId && typeof element.selectVariant === 'function') element.selectVariant(saved.variantId, { silent: true });
        if (element.matches('cart-drawer-upsell') && typeof saved.selected === 'boolean') {
          if (typeof element.setSelected === 'function') element.setSelected(saved.selected);
          else element.dataset.selected = String(saved.selected);
        }
      });

      const terms = this.querySelector('cart-terms-checkbox');
      if (terms && this.memory.termsAccepted) terms.checked = true;
    }

    /* Called by the line items after a quantity change. */
    afterLineChange() {
      if (this.isOpen) reserveClock.start();
    }

    /* ---------- hidden add-ons (toggle upsells & gifts) ---------- */

    rememberToggle(handle, values) {
      const saved = this.memory.toggles.get(handle) || {};
      this.memory.toggles.set(handle, Object.assign(saved, values));
      this.autoSyncCount = 0;
    }

    addonElements() {
      return [...this.querySelectorAll('cart-drawer-upsell[data-toggle="true"], cart-drawer-gift')];
    }

    hiddenHandles() {
      return new Set(this.addonElements().map((element) => element.dataset.handle));
    }

    lineElements(handle) {
      const selector = handle ? `.cart-drawer-item[data-handle="${escapeValue(handle)}"]` : '.cart-drawer-item';
      return [...this.querySelectorAll(selector)];
    }

    visibleLines() {
      const hidden = this.hiddenHandles();
      return this.lineElements().filter((line) => !hidden.has(line.dataset.handle));
    }

    isLastVisibleLine(index) {
      const visible = this.visibleLines();
      return visible.length <= 1 && (visible.length === 0 || Number(visible[0].dataset.index) === Number(index));
    }

    /*
     * Keeps the cart consistent with the add-on widgets after every render:
     * - only hidden add-ons left -> empty the cart
     * - an unlocked gift / selected toggle missing -> add it; a deselected one present -> remove it
     */
    reconcile() {
      // Without an item list there is nothing to compare against.
      if (this.pending > 0 || !this.querySelector('cart-drawer-items')) return;
      const lines = this.lineElements();
      if (lines.length > 0 && this.visibleLines().length === 0) {
        this.clear();
        return;
      }
      if (this.classList.contains('is-empty') || this.autoSyncCount >= MAX_AUTO_SYNC) return;

      const addons = this.addonElements();
      const inCart = (element) => this.lineElements(element.dataset.handle).length > 0;
      const surplus = addons.find((element) => element.dataset.selected !== 'true' && inCart(element));
      const missing = addons.find((element) => element.dataset.selected === 'true' && !inCart(element));
      if (surplus) {
        this.autoSyncCount += 1;
        this.removeProduct(surplus.dataset.handle, surplus);
      } else if (missing) {
        this.autoSyncCount += 1;
        this.addVariant(missing.dataset.id, missing.dataset.sellingPlan, missing);
      }
    }

    async request(url, payload, origin) {
      const api = cartApi();
      if (!api) return null;
      const sectionIds = this.getSectionsToRender().map((entry) => entry.section);
      let state = null;
      let sections = null;
      this.setPending(1, origin);
      try {
        state = await api.postJSON(url, Object.assign({}, payload, { sections: sectionIds, sections_url: window.location.pathname }));
        const problem = api.errorMessage(state);
        if (problem !== null) {
          this.autoSyncCount = MAX_AUTO_SYNC;
          this.reportError(problem || this.dataset.errorText, origin);
          return null;
        }
        sections = state.sections || (await api.fetchSections(sectionIds));
      } catch (error) {
        console.error(error);
        this.reportError(this.dataset.errorText, origin);
        return null;
      } finally {
        this.setPending(-1, origin);
      }
      // Painting may start the next add-on sync, so it happens after the busy state is released.
      this.paint(sections);
      if (this.isOpen) reserveClock.start();
      api.announce({ source: 'cart-drawer', cartData: state, origin: this });
      return state;
    }

    setPending(change, origin) {
      this.pending = Math.max(0, this.pending + change);
      this.classList.toggle('cart-drawer--busy', this.pending > 0);
      if (origin && origin.isConnected && typeof origin.setBusy === 'function') origin.setBusy(change > 0);
    }

    reportError(message, origin) {
      if (origin && origin.isConnected && typeof origin.showError === 'function') {
        origin.showError(message);
        return;
      }
      const holder = document.getElementById('CartDrawer-CartErrors');
      if (holder) holder.textContent = message || '';
    }

    addVariant(variantId, sellingPlan, origin) {
      const api = cartApi();
      if (!api || !variantId) return Promise.resolve(null);
      const item = { id: Number(variantId), quantity: 1 };
      if (sellingPlan) item.selling_plan = Number(sellingPlan);
      return this.request(api.urls.add(), { items: [item] }, origin);
    }

    removeProduct(handle, origin) {
      const api = cartApi();
      const lines = this.lineElements(handle);
      if (!api || lines.length === 0) return Promise.resolve(null);
      const updates = {};
      lines.forEach((line) => {
        updates[line.dataset.variantId] = 0;
      });
      return this.request(api.urls.update(), { updates }, origin);
    }

    /*
     * Replaces the cart line(s) of an add-on with another variant. Without a selling plan this is one
     * /cart/update call; otherwise the old line is removed and reconcile() adds the remembered variant.
     */
    swapVariant(handle, variantId, sellingPlan, origin) {
      const api = cartApi();
      const lines = this.lineElements(handle);
      if (!api || (lines.length === 1 && lines[0].dataset.variantId === String(variantId))) return Promise.resolve(null);
      if (sellingPlan) return this.removeProduct(handle, origin);
      const updates = {};
      lines.forEach((line) => {
        updates[line.dataset.variantId] = 0;
      });
      updates[variantId] = 1;
      return this.request(api.urls.update(), { updates }, origin);
    }

    clear() {
      const api = cartApi();
      if (!api) return Promise.resolve(null);
      return this.request(api.urls.clear(), {});
    }
  }

  if (!customElements.get('cart-reserve-timer')) customElements.define('cart-reserve-timer', CartReserveTimer);
  if (!customElements.get('cart-terms-checkbox')) customElements.define('cart-terms-checkbox', CartTermsCheckbox);
  customElements.define('cart-drawer', CartDrawer);
})();
